import React, { useState } from 'react';
import { X, ShoppingCart, Heart, Minus, Plus, Check } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import ProductSchema from './ProductSchema';

function ProductDetail({ product, isOpen, onClose, saleSettings }) {
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const { addToCart } = useCart();

  if (!product || !isOpen) return null;

  // Sale logic matching ProductGrid
  const globalSaleEnabled = saleSettings?.enabled && saleSettings?.percentage > 0;
  const productSaleEnabled = product.saleEnabled !== false;
  const salePercentage = product.salePercentage || saleSettings?.percentage || 0;

  const isSale = globalSaleEnabled && productSaleEnabled && salePercentage > 0;
  const discount = isSale ? product.price * salePercentage / 100 : 0;
  const salePrice = product.price - discount;

  const handleAddToCart = () => {
    // Create a product object with sale price if applicable
    const productToAdd = {
      ...product,
      price: isSale ? Math.round(salePrice) : product.price,
      originalPrice: product.price,
      saleDiscount: isSale ? salePercentage : 0
    };

    // addToCart now handles all stock validation
    const result = addToCart(productToAdd, quantity);

    // Only show success if it was actually added
    if (result.success) {
      setAddedToCart(true);

      // Reset the added state after 2 seconds
      setTimeout(() => {
        setAddedToCart(false);
      }, 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <ProductSchema product={product} salePrice={isSale ? Math.round(salePrice) : null} />
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 bg-white rounded-full p-2 hover:bg-gray-100 shadow-lg z-10">
          <X size={24} />
        </button>

        <div className="grid md:grid-cols-2 gap-8 p-6">
          <div className="space-y-4">
            <div className="relative bg-gray-100 rounded-lg overflow-hidden">
              <img src={product.images?.[selectedImage] || product.images?.[0]} alt={product.name} className="w-full h-auto object-contain" />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold">{product.name}</h1>
                <p className="text-sm text-gray-600 mt-1">SKU: {product.sku}</p>
              </div>
              <div className="flex flex-col gap-2">
                {isSale && (
                  <div className="bg-red-600 text-white px-3 py-1 rounded-full font-semibold text-sm whitespace-nowrap">
                    {saleSettings.label || 'SALE'} {salePercentage}% OFF
                  </div>
                )}
                {product.newArrival && (
                  <div className="bg-green-600 text-white px-3 py-1 rounded-full font-semibold text-sm whitespace-nowrap">
                    NEW ARRIVAL
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {isSale ? (
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold text-red-600">PKR {Math.round(salePrice).toLocaleString()}</span>
                  <span className="text-xl text-gray-400 line-through">PKR {product.price.toLocaleString()}</span>
                </div>
              ) : (
                <span className="text-3xl font-bold">PKR {product.price.toLocaleString()}</span>
              )}
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">Description</h3>
              <p className="text-gray-700">{product.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-sm mb-1">Fabric</h4>
                <p>{product.fabric}</p>
              </div>
              <div>
                <h4 className="font-semibold text-sm mb-1">Care</h4>
                <p>{product.careInstructions}</p>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Quantity</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center border-2 rounded-lg">
                  <button
                    onClick={() => quantity > 1 && setQuantity(quantity - 1)}
                    className="px-4 py-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={quantity <= 1}
                  >
                    <Minus size={18} />
                  </button>
                  <span className="px-6 py-2 font-semibold">{quantity}</span>
                  <button
                    onClick={() => quantity < (product.stock || 0) && setQuantity(quantity + 1)}
                    className="px-4 py-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={quantity >= (product.stock || 0)}
                  >
                    <Plus size={18} />
                  </button>
                </div>
                <span className={`text-sm font-medium ${
                  (product.stock || 0) === 0
                    ? 'text-red-600'
                    : (product.stock || 0) < 5
                    ? 'text-orange-600'
                    : 'text-gray-600'
                }`}>
                  {(product.stock || 0) === 0
                    ? 'Out of stock'
                    : `${product.stock} available`
                  }
                </span>
              </div>
            </div>

            <button
              onClick={handleAddToCart}
              disabled={(product.stock || 0) === 0}
              className={`w-full py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors ${
                (product.stock || 0) === 0
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : addedToCart
                  ? 'bg-green-600 text-white'
                  : 'bg-[#4a1a1f] text-white hover:bg-[#5a2a2f]'
              }`}
            >
              {(product.stock || 0) === 0 ? (
                'Out of Stock'
              ) : addedToCart ? (
                <>
                  <Check size={20} />
                  Added to Cart!
                </>
              ) : (
                <>
                  <ShoppingCart size={20} />
                  Add to Cart
                </>
              )}
            </button>

            {product.images && product.images.length > 1 ? (
              <div className="grid grid-cols-4 gap-2">
                {product.images.map((img, i) => (
                  <button key={i} onClick={() => setSelectedImage(i)} className={`border-2 rounded-lg overflow-hidden aspect-square ${selectedImage === i ? 'border-gray-800' : 'border-gray-200'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
